import type { MicroCMSImage } from 'microcms-js-sdk';

// area / category は廃止予定の `/areas/{id}/` `/themes/{id}/` にしか遷移先がないため 'unknown' に落とす（JCAT-6198）
export const FeaturedTypeList = ['destination', 'itinerary', 'theme', 'unknown'] as const;
export type FeaturedType = (typeof FeaturedTypeList)[number];

/**
 * MicroCMS 返り値の型定義
 * NOTE: ビューで利用する必要最低限のフィールドに絞った型定義
 * https://dev-calchez.microcms.io/apis/en-featured
 */
export interface MinimumFeaturedResponse {
  id: string; // slugとして使用
  titleEn: string;
  titleJa?: string;
  backgroundWideMedias: MicroCMSImage[];
  backgroundTallMedias: MicroCMSImage[];
  type: string[]; // 単一選択だが、microCMSの仕様上、配列で返ってくる
  /** 説明文（リッチテキスト HTML）。入稿されていない記事もある */
  summary?: string;
  destinationTags?: string[];
  themeTags?: string[];
  /** 旅程タグ設定（days / price はどちらかだけの設定もある） */
  itineraryTagSettings?: {
    days?: number;
    price?: string;
  };
}

export const buildFeaturedType = (type: string): FeaturedType => {
  switch (type) {
    case 'destination':
      return 'destination';
    case 'itinerary':
      return 'itinerary';
    case 'theme':
      return 'theme';
    default:
      return 'unknown';
  }
};

export interface MinimumFeaturedViewModel {
  id: string; // slugとして使用
  titleEn: string;
  titleJa?: string;
  backgroundWideMedias: MicroCMSImage[];
  backgroundTallMedias: MicroCMSImage[];
  type: FeaturedType;
  /** 説明文（リッチテキスト HTML）。入稿されていない記事もある */
  summary?: string;
  destinationTags?: string[];
  themeTags?: string[];
  /** 旅程タグ設定（days / price はどちらかだけの設定もある） */
  itineraryTagSettings?: {
    days?: number;
    price?: string;
  };
}
export type MinimumFeaturedListViewModel = MinimumFeaturedViewModel[];

/**
 * APIレスポンスからビューモデルを作成する
 *
 * @param {MinimumFeaturedResponse} res - APIレスポンス
 * @returns {MinimumFeaturedViewModel} - ビューモデル
 */
export const buildMinimumFeaturedViewModel = (res: MinimumFeaturedResponse): MinimumFeaturedViewModel => {
  return {
    id: res.id,
    titleEn: res.titleEn,
    titleJa: res.titleJa,
    backgroundWideMedias: res.backgroundWideMedias,
    backgroundTallMedias: res.backgroundTallMedias,
    type: buildFeaturedType(res.type[0]), // 単一選択なので、配列の0番目を取得
    summary: res.summary,
    destinationTags: res.destinationTags,
    themeTags: res.themeTags,
    itineraryTagSettings: res.itineraryTagSettings,
  };
};

/**
 * リスト表示用のビューモデルを組み立てる
 */
export const buildMinimumFeaturedListViewModel = (res: MinimumFeaturedResponse[] | undefined): MinimumFeaturedListViewModel => {
  if (!res) {
    return [];
  }
  return res.map(buildMinimumFeaturedViewModel);
};

/**
 * 背景画像のURLを取得する
 * tallMediasを優先し、なければwideMediasを使用
 *
 * @param {MinimumFeaturedViewModel} featured - ビューモデル
 * @returns {string} - 画像URL
 */
export const getBackgroundImageUrl = (featured: MinimumFeaturedViewModel): string => {
  if (featured.backgroundTallMedias && featured.backgroundTallMedias.length > 0) {
    return featured.backgroundTallMedias[0].url;
  }
  if (featured.backgroundWideMedias && featured.backgroundWideMedias.length > 0) {
    return featured.backgroundWideMedias[0].url;
  }
  return '';
};

/**
 * 詳細ページへのリンクを生成する
 *
 * @param {MinimumFeaturedViewModel} featured - ビューモデル
 * @returns {string} - リンク
 */
export const generateDetailPageLink = (featured: MinimumFeaturedViewModel) => {
  switch (featured.type) {
    case 'destination':
      return `/${featured.id}/`;
    case 'itinerary':
      return `/itineraries/${featured.id}/`;
    case 'theme':
      return `/japan/${featured.id}/`;
  }
  return '';
};
